<template>
  <div class="qr-reader">
    <video
      ref="webcamRef"
      class="qr-reader-video"
      autoplay
      playsinline
      crossorigin="anonymous"
    />
    <div
      ref="webcamOverlayRef"
      :style="{
        width: `${scanRegionArea.width}px`,
        height: `${scanRegionArea.height}px`,
        top: `${scanRegionArea.y}px`,
        left: `${scanRegionArea.x}px`,
        display: scanRegionArea.width && scanRegionArea.height ? 'block' : 'none',
      }"
      class="qr-reader-overlay"
    >
      <i class="qr-reader-overlay-corners" />
    </div>
    <svg
      width="0"
      height="0"
      viewBox="0 0 100 100"
    >
      <defs>
        <clipPath
          id="qrCodeOverlayCorners"
          clipPathUnits="objectBoundingBox"
        >
          <path d="M0,0 h0.1 v0.1 h-0.1" />
          <path d="M1,0 v0.1 h-0.1 v-0.1" />
          <path d="M1,1 v-0.1 h-0.1 v0.1" />
          <path d="M0,1 h0.1 v-0.1 h-0.1" />
        </clipPath>
      </defs>
    </svg>
  </div>
</template>

<script>
import {
  defineComponent,
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import jsonpath from 'jsonpath';
import { setOrientationChangeListener, removeOrientationChangeListener } from '@ht/lib-plural-vue';
import useSequence from '@/composables/useSequence';
import useQrReader from '@/composables/useQrReader';
import useActions from '@/composables/useActions';
import useConfig from '@/composables/useConfig';
import { QR_READER_TIMEOUT, VARIABLE_VALUE_TYPES } from '@/js/constants';
import useContent from '@/composables/useContent';

export default defineComponent({
  name: 'QrReaderComponent',

  setup() {
    const webcamRef = ref(null);
    const webcamOverlayRef = ref(null);
    const { runSequence } = useSequence();
    const {
      qrCodeContent,
      initScanner,
      startScan,
      stopScan,
      destroy,
      restart,
      scanRegionArea,
    } = useQrReader();
    const { currentAction } = useActions();
    const { setVariables, setFlowStateBackToStart } = useConfig();
    const { isPaused } = useContent();

    let timeout = null;

    function handleError() {
      clearTimeout(timeout);
      if (currentAction.value.params.failureFlow) {
        setFlowStateBackToStart(currentAction.value.params.failureFlow);
        runSequence(currentAction.value.params.failureFlow);
      }
    }

    function startTimeout() {
      timeout = setTimeout(async () => {
        stopScan();
        handleError();
      }, QR_READER_TIMEOUT);
    }

    async function startWithTimeout() {
      try {
        await startScan();
        startTimeout();
      } catch (e) {
        handleError();
      }
    }

    function handleSuccess(value) {
      try {
        clearTimeout(timeout);
        const payload = JSON.parse(value);

        const { variables } = currentAction.value.params;
        if (variables.length) {
          const variablesToSet = variables.reduce((acc, { id, parameter }) => {
            const mappedValue = jsonpath.value(payload, parameter);
            if (mappedValue !== undefined) {
              acc.push({ id, value: { value: mappedValue, type: VARIABLE_VALUE_TYPES.PRIMITIVE } });
            }

            return acc;
          }, []);
          setVariables(variablesToSet);
        }

        if (currentAction.value.params.successFlow) {
          setFlowStateBackToStart(currentAction.value.params.successFlow);
          runSequence(currentAction.value.params.successFlow);
        }
      } catch (e) {
        // not JSON as payload, treat as error
        handleError();
      }
    }

    const unwatch = watch(qrCodeContent, (value) => {
      stopScan();
      handleSuccess(value);
      unwatch();
    });

    onMounted(async () => {
      const isSuccess = await initScanner(webcamRef.value, {
        overlay: webcamOverlayRef.value,
        calculateScanRegion: (videoEl) => {
          const regionSizePercentage = 0.6; // slightly lower than default of 2/3
          const regionSize = Math.min(videoEl.videoHeight, videoEl.videoWidth) * regionSizePercentage;
          const x = Math.round((videoEl.videoWidth - regionSize) / 2);
          const y = Math.round((videoEl.videoHeight - regionSize) / 2);
          return { x, y, width: regionSize, height: regionSize };
        },
      });

      if (!isSuccess) {
        if (currentAction.value.params.failureFlow) {
          setFlowStateBackToStart(currentAction.value.params.failureFlow);
          runSequence(currentAction.value.params.failureFlow);
        }
        return;
      }

      await startWithTimeout();
      setOrientationChangeListener(restart);
    });

    onBeforeUnmount(() => {
      destroy();
      removeOrientationChangeListener(restart);
      clearTimeout(timeout);
    });

    watch(isPaused, () => {
      if (isPaused.value) {
        clearTimeout(timeout);
      } else {
        startTimeout();
      }
    });

    return {
      webcamRef,
      webcamOverlayRef,
      scanRegionArea,
    };
  },
});
</script>

<style lang="scss" scoped>

.qr-reader {
  position: fixed;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  background: #2A3746;

  &-video {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  &-overlay {
    display: none;
    position: absolute;
    outline: rgba(0, 0, 0, .4) solid 50vmax;
    pointer-events: none;

    &-corners {
      width: 100%;
      height: 100%;
      border: 8px solid white;
      position: relative;
      display: block;
      transform: scale(1.1);
      border-radius: 10px;
      clip-path: url(#qrCodeOverlayCorners);

      @media screen and (max-width: $desktop_s_breakpoint) {
        border: 6px solid white;
        border-radius: 6px;
      }

      @media screen and (orientation: portrait) and (max-width: $tablet_breakpoint) {
        border: 5px solid white;
        border-radius: 5px;
      }

      @media screen and (orientation: portrait) and (max-width: $mobile_breakpoint) {
        border: 4px solid white;
        border-radius: 4px;
      }
    }
  }
}
</style>
